import React, {useContext, useEffect, useState} from 'react';
import {Table} from "antd";
import dayjs from "dayjs";
import axios from "@common/axios";
import {BrandContext} from "@context/BrandContext";
import {UserContext} from "@context/UserContext";
import TableAntD from "@common/TableAntD";
import ExportToCsvButton from '@common/ExportToCsvButton';
import CircularLoader from "@common/CircularLoader";

const StudentReportActivity = ({ currentStudentId, dateRange, showSessionsWithActivity }) => {
    const [loading, setLoading] = useState(true);
    const brandConfig = useContext(BrandContext);
    const { user } = useContext(UserContext);
    const [data, setData] = useState([]);
    const [studentName, setStudentName] = useState();

    useEffect(() => {
        const fetchActivityReport = async () => {
            try {
                setLoading(true);
                const [start, end] = dateRange || [];

                const isUSLocale = user.locale === 'en_US';

                let formattedStart, formattedEnd;

                if (start) {
                    const day = start.date().toString().padStart(2, '0');
                    const month = (start.month() + 1).toString().padStart(2, '0');
                    const year = start.year();
                    formattedStart = isUSLocale ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
                }

                if (end) {
                    const day = end.date().toString().padStart(2, '0');
                    const month = (end.month() + 1).toString().padStart(2, '0');
                    const year = end.year();
                    formattedEnd = isUSLocale ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
                }

                const response = await axios.get('/web/ui/report/teacher/activity/student', {
                    params: {
                        studentId: currentStudentId,
                        ...(formattedStart && { startSearch: formattedStart }),
                        ...(formattedEnd && { endSearch: formattedEnd }),
                        ...((formattedStart && formattedEnd) && { searchPeriod: 'DATE_RANGE' })
                    }
                });
                const rawData = response.data.reportData || [];
                const processed = rawData.flatMap(flattenActivityData);
                setData(processed);
                setStudentName(response.data.studentName);
            } catch (error) {
                console.error('Failed to fetch session data:', error);
            }
            setLoading(false)
        };
        if(currentStudentId){
            fetchActivityReport();
        }
    }, [currentStudentId, dateRange, showSessionsWithActivity]);

    const flattenActivityData = (session) => {
        const activityTypes = [
            { key: 'questions', singular: 'Question', plural: 'Questions', countKey: 'countQuestions' },
            { key: 'quizzes', singular: 'Quiz', plural: 'Quizzes' },
            { key: 'walkthroughs', singular: 'Walkthrough', plural: 'Walkthroughs', countKey: 'countQuestions' },
            { key: 'inlineExercises', singular: 'Inline Exercise', plural: 'Inline Exercises' },
            { key: 'algebraKits', singular: 'Algebra Kit', plural: 'Algebra Kits' },
            { key: 'tests', singular: brandConfig.testName, plural: brandConfig.testsName },
            { key: 'exercises', singular: 'Exercise', plural: 'Exercises' },
            { key: 'scorchers', singular: 'Scorcher', plural: 'Scorchers', countKey: 'countScorchers' },
        ];

        const rows = [];

        activityTypes.forEach(({ key, singular, plural, countKey }) => {
            const items = session[key] || [];

            items.forEach((item) => {
                const count = countKey ? item[countKey] : 1;
                const name = item.conceptName || item.testName || 'Unknown';
                const label = count === 1 ? singular : plural;

                rows.push({
                    type: singular,
                    name,
                    activity: `${count} ${label}`,
                });
            });
        });

        if (rows.length === 0 && !showSessionsWithActivity) {
            rows.push({
                type: '',
                name: '',
                activity: 'No activity recorded',
            });
        }

        return rows.map((entry, index) => ({
            key: `${session.userSessionId}-${index}`,
            sessionDate: session.startDate,
            duration: session.sessionTime,
            rowSpan: index === 0 ? rows.length : 0,
            ...entry,
        }));
    };

    const columns = [
        {
            title: 'Session date',
            dataIndex: 'sessionDate',
            key: 'sessionDate',
            render: (timestamp) => dayjs(timestamp).format('YYYY-MM-DD HH:mm'),
            onCell: (record) => ({
                rowSpan: record.rowSpan,
            }),
        },
        {
            title: 'Duration',
            dataIndex: 'duration',
            key: 'duration',
            onCell: (record) => ({
                rowSpan: record.rowSpan,
            }),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            onCell: (record) => {
                if (record.activity === 'No activity recorded') {
                    return { colSpan: 0 };
                }
                return {};
            },
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            onCell: (record) => {
                if (record.activity === 'No activity recorded') {
                    return { colSpan: 0 };
                }
                return {};
            },
        },
        {
            title: 'Activity',
            dataIndex: 'activity',
            key: 'activity',
            render: (text) => text,
            onCell: (record) => {
                if (record.activity === 'No activity recorded') {
                    return { colSpan: 3 }; // Merge Type + Name + Activity
                }
                return {};
            },
        },
    ];

    const flattenDataForExport = (tableData) => {
        let processedData = [];
        let currentSessionInfo = {};

        tableData.forEach(record => {
            // If it's a new session group, capture the session info
            if (record.rowSpan > 0) {
                currentSessionInfo = {
                    sessionDate: dayjs(record.sessionDate).format('YYYY-MM-DD HH:mm'),
                    duration: record.duration
                };
            }

            // For "No activity", merge columns as in the table
            if (record.activity === 'No activity recorded') {
                processedData.push({
                    ...currentSessionInfo,
                    type: '',
                    name: '',
                    activity: 'No activity recorded'
                });
            } else {
                // For regular rows, combine with the current session info
                processedData.push({
                    ...currentSessionInfo,
                    type: record.type,
                    name: record.name,
                    activity: record.activity
                });
            }
        });
        return processedData;
    };

    if(loading){
        return(
            <CircularLoader />
        )
    }


    return (
        <>
            <div className={'ch-reports-heading-wrapper'}>
                <h3>{studentName?.split(' ')[0]}'s activity</h3>
                <ExportToCsvButton
                    data={flattenDataForExport(data)}
                    columns={columns}
                    fileName={`${studentName || 'student'}_activity_report.csv`}
                    disabledTooltipText={'This student has no data to export. Choose a different date range or student.'}
                    disabled={data.length === 0}
                />
            </div>
            <TableAntD
                columnData={columns}
                dataSource={data}
                pagination={false}
                bordered
                size={"small"}
                locale={{ emptyText: 'No activity for the selected date range' }}
                loading={loading}
                dynamicWidth={true}
            />
        </>
    );
}

export default StudentReportActivity;